/**
 * A whole line that is nothing but a single markdown image, possibly indented.
 * Notion indents images nested under list items/toggles; 4+ leading spaces make
 * CommonMark treat the line as an indented code block, so BlockNote drops the
 * image entirely. `.*` (greedy) for the URL tolerates balanced parens Notion
 * leaves in asset paths, e.g. `AI Ideas (search engine)/image.png`.
 */
const STANDALONE_IMAGE_LINE = /^[ \t]+(!\[[^\]]*\]\(.*\))[ \t]*$/gm;

/** An empty-label markdown link, e.g. `[](https://…)`. */
const EMPTY_LABEL_LINK = /\[\]\((https?:\/\/[^\s)]+)\)/g;

const IMAGE_URL_EXTENSION = /\.(png|jpe?g|gif|webp|svg|bmp|avif|tiff?|ico|heic|heif)(?:$|[?#])/i;

/**
 * Notion exports externally-hosted images (e.g. pasted from Google Docs) as
 * empty-label links pointing at the remote image, not as `![](…)`. Detect the
 * ones that are really images so we can promote them; leave real link embeds
 * such as `[](https://www.notion.so)` untouched.
 */
function looksLikeImageUrl(rawUrl: string): boolean {
  if (IMAGE_URL_EXTENSION.test(rawUrl)) {
    return true;
  }
  try {
    const host = new URL(rawUrl).hostname.toLowerCase();
    return host === 'googleusercontent.com' || host.endsWith('.googleusercontent.com');
  } catch {
    return false;
  }
}

/** Notion inline-database view config leaked into markdown exports. */
const INLINE_DB_VIEW_METADATA =
  /^filters:\s*\r?\n(?:.*\r?\n)*?(?:^sort:\s*\r?\n(?:.*\r?\n)*?)?(?=^#|\r?\n\[|\r?\n\r?\n|$)/gim;

/** Light normalisation so Notion export quirks parse reliably in BlockNote. */
export function normalizeNotionMarkdownForImport(markdown: string): string {
  return markdown
    .replace(/^\uFEFF/, '')
    .replace(/\u00a0/g, ' ')
    .replace(/\r\n/g, '\n')
    .replace(INLINE_DB_VIEW_METADATA, '')
    .replace(EMPTY_LABEL_LINK, (full, url: string) =>
      looksLikeImageUrl(url) ? `![](${url})` : full,
    )
    .replace(STANDALONE_IMAGE_LINE, '$1');
}
